"use client";

import Link from "next/link";
import { useState, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import Image from "next/image";
import { Typography } from "@/components/ui/typography";
import Tabs, { TabPanel } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import Icon from "@/components/ui/Icon";
import { TranslationDeliverablesCardLink } from "@/components/screen/global-components/translation-deliverables";
import { ROUTES } from "@/constants/routes";
import { normalizeUrl } from "@/utils/normalizeUrl";

type RawItem = {
  fields?: {
    linkCategory?: string;
    icon?: {
      fields?: {
        title?: string;
        file?: {
          url?: string;
        };
      };
    };
  };
};

type RawCategory = {
  fields?: {
    nameCategory?: string;
    categoryData?: RawItem[];
  };
};

type DocumentsHeroBlockProps = {
  content?: {
    translationDeliverables?: string;
    category?: RawCategory[];
  };
};

export type TranslationDeliverablesItemProps = {
  data?: { id: number; name: string; url: string; img?: string }[];
} & React.ComponentProps<"div">;

const TranslationDeliverablesItem = ({
  data = [],
  className,
}: TranslationDeliverablesItemProps) => {
  const notMobile = useMediaQuery("(min-width: 640px)");
  const eighthItemRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");
  const shouldEnableMobileCollapseRef = useRef(data?.length > 8);

  useLayoutEffect(() => {
    if (notMobile || !shouldEnableMobileCollapseRef.current) {
      setHeight("auto");
      return;
    }

    if (expanded && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else if (eighthItemRef.current) {
      const { offsetTop, clientHeight } = eighthItemRef.current;
      setHeight(offsetTop + clientHeight);
    }
  }, [expanded, notMobile, shouldEnableMobileCollapseRef.current]);

  useEffect(() => {
    if (
      notMobile ||
      !shouldEnableMobileCollapseRef.current ||
      !eighthItemRef.current
    ) {
      return;
    }

    const resizeHandler = () => {
      if (eighthItemRef.current && !expanded) {
        const { offsetTop, clientHeight } = eighthItemRef.current;
        setHeight(offsetTop + clientHeight);
      }
    };

    resizeHandler();

    window.addEventListener("resize", resizeHandler, { passive: true });

    return () => {
      window.removeEventListener("", resizeHandler);
    };
  }, [expanded, notMobile, shouldEnableMobileCollapseRef.current]);

  return (
    <div className={className}>
      <motion.div
        animate={{ height: notMobile ? "auto" : height }}
        transition={{
          duration:
            notMobile || !shouldEnableMobileCollapseRef.current ? 0 : 0.5,
          ease: "easeInOut",
        }}
        className="overflow-hidden sm:overflow-visible sm:!h-auto"
        ref={contentRef}
      >
        <div
          className={cn(
            "flex flex-wrap justify-center -m-2 sm:[&>:nth-child(n+9)]:!flex sm:!max-w-none",
            {
              "[&>:nth-child(n+9)]:hidden":
                !shouldEnableMobileCollapseRef.current,
            }
          )}
        >
          {data.map((item, index) => {
            return (
              <TranslationDeliverablesCardLink
                key={item.id}
                ref={index === 7 ? eighthItemRef : null}
                href={"documents/" + item.url}
                imageUrl={item?.img}
                imageAlt={item.name}
                className="p-2 w-[50%] sm:w-[33.33%] md:w-[25%] lg:w-[20%] flex"
              >
                {item.name}
              </TranslationDeliverablesCardLink>
            );
          })}
        </div>
      </motion.div>
      {shouldEnableMobileCollapseRef.current && (
        <div className="mt-6 flex sm:hidden">
          <Button
            variant="primary-outline"
            className={cn(
              "flex-auto !py-3 !px-8 min-w-0 flex items-center justify-between gap-2 h-[48]",
              {
                "[&>svg]:-rotate-180": expanded,
              }
            )}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={expanded ? "less" : "more"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {expanded ? "View Less" : "View More"}
              </motion.span>
            </AnimatePresence>
            <Icon
              id="icon-arrow-down-lg_24"
              width={24}
              height={24}
              className="shrink-0 !w-6 !h-6 transform will-change-transform transition-transform"
            />
          </Button>
        </div>
      )}
    </div>
  );
};

export default function TranslationDeliverables({
  content,
}: DocumentsHeroBlockProps) {
  const translationDeliverables = content?.translationDeliverables ?? "";
  const category = content?.category ?? [];

  const [activeTab, setActiveTab] = useState<string>("");
  console.log("category", category);
  const tabs = useMemo(() => {
    return category
      .map((cat) => {
        const key = cat.fields?.nameCategory?.toLowerCase();
        const label = cat.fields?.nameCategory;
        if (!key || !label) return null;
        return { key, label };
      })
      .filter((t): t is { key: string; label: string } => t !== null);
  }, [category]);

  useEffect(() => {
    if (!activeTab && tabs.length > 0) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs, activeTab]);

  const deliverables = useMemo(() => {
    const result: Record<
      string,
      { id: number; name: string; url: string; img?: string }[]
    > = {};

    category.forEach((cat) => {
      const key = cat.fields?.nameCategory?.toLowerCase();
      const list = cat.fields?.categoryData ?? [];

      if (!key) return;

      result[key] = list
        .filter((item) => item?.fields?.linkCategory)
        .map((item, idx) => ({
          id: idx,
          name: item.fields?.icon?.fields?.title ?? item.fields!.linkCategory!,
          url: normalizeUrl(item.fields!.linkCategory!),
          img: item.fields?.icon?.fields?.file?.url
            ? "https:" + item.fields.icon.fields.file.url
            : undefined,
        }));
    });

    return result;
  }, [category]);

  const currentTab = activeTab || tabs[0]?.key;

  return (
    <section className="section bg-white lg:bg-background-secondary">
      <div className="container mx-auto py-12 lg:py-20">
        <Typography
          variant="headingMain"
          className="w-full md:text-center lg:text-[40px] lg:leading-[150%]"
        >
          <h2>{translationDeliverables || "Document deliverables"}</h2>
        </Typography>
        <div className="mt-4 md:mt-8 lg:mt-12">
          {!!tabs?.length ? (
            <Tabs
              defaultValue={currentTab}
              tabsTriggers={tabs}
              tablistClassName="p-1 rounded-3xl bg-background-secondary border border-[#dadada] flex-auto min-w-0"
              tabIndicatorClassName="flex bg-white border border-blue rounded-3xl"
              tabTriggerClassName="flex-auto min-w-0 flex items-center justify-center p-2.5 text-base leading-[130%] text-semantic-text-primary data-[state='active']:text-blue-500 data-[state='active']:font-bold [&>span>[aria-hidden='true']]:font-bold"
              tablistWrapClassName="flex-auto w-full max-w-[706]"
            >
              <div className="flex">
                {Object.keys(deliverables ?? {})?.map((key, i) => {
                  const data = deliverables[key];

                  return (
                    <TabPanel
                      key={i}
                      value={key}
                      className="mt-6 md:mt-10 lg:mt-12"
                    >
                      {!!data?.length ? (
                        <TranslationDeliverablesItem data={data} />
                      ) : (
                        <Typography
                          variant="bodyLg"
                          className="text-center text-gray-500 py-6"
                          asChild
                        >
                          <p>No categories available</p>
                        </Typography>
                      )}
                    </TabPanel>
                  );
                })}
              </div>
            </Tabs>
          ) : (
            <Typography
              variant="bodyLg"
              className="text-center text-gray-500 py-6"
              asChild
            >
              <p>No categories available</p>
            </Typography>
          )}
        </div>

        <div className="mt-6 md:mt-4 text-base leading-[130%] md:leading-[150%] text-center md:py-4 max-w-[436px] m-auto">
          Don’t see the deliverable you need? <br className="md:hidden" /> We
          can still help. Contact us to source a translator for your project.{" "}
          <Link
            className="text-blue-500 font-bold underline md:font-medium md:no-underline"
            href={ROUTES.getQuote}
          >
            Get a quote
          </Link>
        </div>
      </div>
    </section>
  );
}
